import React, { useEffect, useRef, Component } from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';
import { SocialProofPopup } from '../components/SocialProofPopup';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/aUZf5itFxr9pLnnrjJHMnZ/CABEZA_EN_CALMA.png';
const PACK_IMAGE = 'https://cdn.magicpatterns.com/uploads/fC9JowzmJxiFm1Yo7y746U/MOCK_UP_PACK_CABEZA_EN_CALMA.png';
const CHECKOUT_URL = 'https://checkout.gaiadigitalpro.com/checkouts/cn/hWN7i77Lz9U6GcXZhnwrolvt/es-ar?_r=AQABrUjfsdU2T29yVtWx15vLoAfT7X0wdfbRNCU47XMOaIY&preview_theme_id=163059597347';
// Colors
const COLORS = {
  bg: '#F5F1E8',
  textPrimary: '#2C2C2C',
  textSecondary: '#6B6B6B',
  accent: '#8B9D83',
  accentHover: '#6B7A63'
};
// Components
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('opacity-100', 'translate-y-0');
        entry.target.classList.remove('opacity-0', 'translate-y-8');
      }
    }, {
      threshold: 0.1
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, []);
  return <section id={id} ref={ref} className={`opacity-0 translate-y-8 transition-all duration-1000 ease-out ${className}`} data-id="element-10">
      {children}
    </section>;
};
const Button = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`inline-flex items-center justify-center px-8 py-4 text-white font-medium tracking-wide rounded-full transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg ${className}`} style={{
  backgroundColor: COLORS.accent
}} onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.accentHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.accent} data-id="element-11">
    {children}
  </a>;
export function LandingPage() {
  return <div className="min-h-screen font-sans selection:bg-[#8B9D83]/30" style={{
    backgroundColor: COLORS.bg,
    color: COLORS.textPrimary
  }} data-id="element-12">
      <SocialProofPopup data-id="element-13" />

      {/* Hero Section */}
      <div className="relative pt-12 pb-20 px-6 md:pt-20 md:pb-32 overflow-hidden" data-id="element-14">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20" data-id="element-15">
          <div className="flex-1 text-center md:text-left z-10" data-id="element-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium leading-tight mb-6 text-[#2C2C2C]" data-id="element-17">
              Pensar menos. <br data-id="element-18" />
              Descansar de verdad. <br data-id="element-19" />
              <span className="italic text-[#6B6B6B]" data-id="element-20">
                Volver a sentir calma en tu mente.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-[#6B6B6B] mb-10 max-w-lg mx-auto md:mx-0 leading-relaxed" data-id="element-21">
              Una guía para soltar la carga mental y pensar menos sin exigirte
              más.
            </p>
            <Button className="text-lg w-full md:w-auto min-w-[240px]" data-id="element-22">
              ELIJO MÁS CALMA
            </Button>
          </div>
          <div className="flex-1 w-full max-w-md md:max-w-full relative z-10" data-id="element-23">
            <a href="#compra" className="block transition-transform duration-500 hover:scale-[1.02] cursor-pointer" data-id="element-24">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-[#8B9D83]/20" data-id="element-25">
                <img src={HERO_IMAGE} alt="Cabeza en Calma Libro" className="w-full h-auto object-cover" data-id="element-26" />
              </div>
            </a>
          </div>
        </div>

        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none opacity-40" data-id="element-27">
          <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] rounded-full bg-white blur-[100px]" data-id="element-28" />
        </div>
      </div>

      {/* Emotional Identification */}
      <Section className="py-20 px-6 bg-white/40" data-id="element-29">
        <div className="max-w-3xl mx-auto text-center" data-id="element-30">
          <h2 className="text-3xl md:text-4xl font-serif mb-8 text-[#2C2C2C]" data-id="element-31">
            Si sentís que tu cabeza nunca se apaga, no estás sola.
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#4A4A4A] leading-relaxed" data-id="element-32">
            <p data-id="element-33">
              Pensás todo el día. Mientras trabajás. Mientras cuidás. Mientras
              intentás descansar.
            </p>
            <p data-id="element-34">Podés frenar el cuerpo, pero tu mente sigue.</p>
            <p data-id="element-35">
              Listas mentales. Pendientes invisibles. Cosas que ya pasaron.
              Cosas que podrían salir mal.
            </p>
            <p className="font-medium text-[#2C2C2C] pt-4" data-id="element-36">
              Desde afuera parecés fuerte. Por dentro, estás cansada.
            </p>
          </div>
        </div>
      </Section>

      {/* Reframe Section */}
      <Section className="py-20 px-6" data-id="element-37">
        <div className="max-w-3xl mx-auto text-center" data-id="element-38">
          <h2 className="text-3xl md:text-4xl font-serif mb-8 text-[#2C2C2C]" data-id="element-39">
            El problema no sos vos.
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#4A4A4A] leading-relaxed" data-id="element-40">
            <p data-id="element-41">
              No te falta fuerza. No te falta disciplina. No te falta voluntad.
            </p>
            <p data-id="element-42">
              Lo que te pasa es que nadie te enseñó a calmar la mente sin
              exigirte más.
            </p>
            <p data-id="element-43">
              Cabeza en Calma no busca que pienses positivo ni que apagues
              pensamientos a la fuerza.
            </p>
            <p className="font-medium text-[#2C2C2C] pt-4 text-2xl font-serif italic" data-id="element-44">
              Te enseña a relacionarte distinto con tu mente.
            </p>
          </div>
        </div>
      </Section>

      {/* What is it & Benefits */}
      <Section className="py-24 px-6 bg-[#8B9D83]/10" data-id="element-45">
        <div className="max-w-5xl mx-auto" data-id="element-46">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-47">
            <div data-id="element-48">
              <h2 className="text-3xl md:text-4xl font-serif mb-6 text-[#2C2C2C]" data-id="element-49">
                ¿Qué es Cabeza en Calma?
              </h2>
              <p className="text-lg text-[#4A4A4A] mb-8 leading-relaxed" data-id="element-50">
                Cabeza en Calma es un programa digital práctico que te guía paso
                a paso para:
              </p>
              <ul className="space-y-4 mb-8" data-id="element-51">
                {['Reducir el ruido mental', 'Calmar la ansiedad cotidiana', 'Descansar sin culpa', 'Recuperar claridad emocional'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-52">
                    <div className="mt-1 p-1 rounded-full bg-[#8B9D83]/20 text-[#8B9D83]" data-id="element-53">
                      <Check size={16} strokeWidth={3} data-id="element-54" />
                    </div>
                    <span className="text-lg text-[#2C2C2C]" data-id="element-55">{item}</span>
                  </li>)}
              </ul>
              <p className="text-[#6B6B6B] italic border-l-4 border-[#8B9D83] pl-4 py-1" data-id="element-56">
                Todo con ejercicios simples y sin experiencia previa.
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-[#8B9D83]/20" data-id="element-57">
              <h3 className="text-xl font-serif mb-6 text-center text-[#2C2C2C]" data-id="element-58">
                Beneficios que vas a sentir
              </h3>
              <ul className="space-y-5" data-id="element-59">
                {['Menos pensamientos repetitivos', 'Más calma interna', 'Mayor claridad', 'Descanso mental real', 'Menos autoexigencia'].map((benefit, i) => <li key={i} className="flex items-center gap-3" data-id="element-60">
                    <Check size={20} className="text-[#8B9D83]" data-id="element-61" />
                    <span className="text-lg text-[#4A4A4A]" data-id="element-62">{benefit}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* What's Included */}
      <Section className="py-24 px-6" data-id="element-63">
        <div className="max-w-4xl mx-auto text-center" data-id="element-64">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-[#2C2C2C]" data-id="element-65">
            ¿Qué incluye?
          </h2>

          <div className="mb-12" data-id="element-66">
            <a href="#compra" className="block hover:opacity-95 transition-opacity cursor-pointer" data-id="element-67">
              <img src={PACK_IMAGE} alt="Pack Completo Cabeza en Calma" className="w-full h-auto rounded-xl shadow-lg mb-10" data-id="element-68" />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-69">
            {['Ebook principal "Cabeza en Calma"', 'Acceso inmediato y de por vida', 'Ejercicios prácticos y simples'].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-[#EAEAEA] flex flex-col items-center justify-center h-full" data-id="element-70">
                <Star className="text-[#8B9D83] mb-3 w-8 h-8 opacity-60" data-id="element-71" />
                <p className="font-medium text-[#2C2C2C]" data-id="element-72">{item}</p>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Bonuses */}
      <Section className="py-20 px-6 bg-white" data-id="element-73">
        <div className="max-w-4xl mx-auto" data-id="element-74">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center text-[#2C2C2C]" data-id="element-75">
            Bonos de Regalo
          </h2>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-76">
            {[{
            title: 'BONO 1 – Pensar vs Rumiar',
            price: '$6.999'
          }, {
            title: 'BONO 2 – Primeros Auxilios Mentales',
            price: '$6.999'
          }, {
            title: 'BONO 3 – Descansar sin Culpa',
            price: '$6.999'
          }, {
            title: 'BONO 4 – Diario de Descarga Mental Suave',
            price: '$6.999'
          }].map((bonus, i) => <div key={i} className="border border-[#8B9D83]/30 rounded-xl p-6 bg-[#F9F7F2] hover:shadow-md transition-shadow" data-id="element-77">
                <h3 className="font-serif text-xl mb-3 text-[#2C2C2C]" data-id="element-78">
                  {bonus.title}
                </h3>
                <p className="text-[#6B6B6B]" data-id="element-79">
                  Precio regular:{' '}
                  <span className="line-through decoration-red-400 decoration-1" data-id="element-80">
                    {bonus.price}
                  </span>
                </p>
                <p className="text-[#8B9D83] font-bold mt-1" data-id="element-81">
                  GRATIS por el día de hoy
                </p>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Purchase Section */}
      <Section id="compra" className="py-24 px-6 bg-[#2C2C2C] text-[#F5F1E8]" data-id="element-82">
        <div className="max-w-3xl mx-auto text-center" data-id="element-83">
          <div className="mb-10" data-id="element-84">
            <span className="inline-block px-4 py-1 rounded-full border border-[#8B9D83] text-[#8B9D83] text-sm tracking-wider mb-4" data-id="element-85">
              OFERTA ESPECIAL
            </span>
            <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-86">
              $13.999{' '}
              <span className="text-2xl text-[#6B6B6B] font-sans" data-id="element-87">ARS</span>
            </h2>
            <p className="text-[#A0A0A0] text-lg max-w-xl mx-auto" data-id="element-88">
              Acceso completo al programa, todos los bonos y actualizaciones
              futuras. Un único pago.
            </p>
          </div>

          <Button className="w-full md:w-auto text-xl px-12 py-5 shadow-[0_0_30px_rgba(139,157,131,0.3)]" data-id="element-89">
            ELIJO MÁS CALMA – $13.999
          </Button>

          <div className="mt-8 flex justify-center gap-4 text-sm text-[#6B6B6B]" data-id="element-90">
            <span className="flex items-center gap-1" data-id="element-91">
              <Check size={14} data-id="element-92" /> Acceso inmediato
            </span>
            <span className="flex items-center gap-1" data-id="element-93">
              <Check size={14} data-id="element-94" /> Compra segura
            </span>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="py-24 px-6" data-id="element-95">
        <div className="max-w-6xl mx-auto" data-id="element-96">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center text-[#2C2C2C]" data-id="element-97">
            Lo que dicen quienes ya lo probaron
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-98">
            {[{
            text: 'Por primera vez siento que mi mente descansa sin esfuerzo.',
            author: 'Laura',
            details: '38 años – Buenos Aires'
          }, {
            text: 'Me sentí acompañada, no exigida.',
            author: 'Marina',
            details: '42 años – Rosario'
          }, {
            text: 'Dejé de pensar que había algo mal en mí.',
            author: 'Cecilia',
            details: '35 años – Córdoba'
          }, {
            text: 'Es simple, humano y real.',
            author: 'Andrea',
            details: '50 años – Mendoza'
          }, {
            text: 'Lo uso cuando siento que la cabeza no para.',
            author: 'Natalia',
            details: '33 años – La Plata'
          }].map((testimonial, i) => <div key={i} className={`bg-white p-8 rounded-2xl shadow-sm border border-[#EAEAEA] flex flex-col ${i >= 3 ? 'md:col-span-1 md:last:col-span-1 md:last:col-start-2 lg:col-span-1 lg:last:col-auto' : ''}`} data-id="element-99">
                <div className="flex-1" data-id="element-100">
                  <div className="flex gap-1 mb-4" data-id="element-101">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} size={14} className="fill-[#8B9D83] text-[#8B9D83]" data-id="element-102" />)}
                  </div>
                  <p className="text-lg text-[#4A4A4A] italic mb-6" data-id="element-103">
                    "{testimonial.text}"
                  </p>
                </div>
                <div className="border-t border-[#F5F1E8] pt-4" data-id="element-104">
                  <p className="font-medium text-[#2C2C2C]" data-id="element-105">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-[#6B6B6B]" data-id="element-106">
                    {testimonial.details}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-24 px-6 text-center" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <h2 className="text-3xl md:text-4xl font-serif mb-10 text-[#2C2C2C]" data-id="element-109">
            No tenés que vivir con la cabeza en ruido.
          </h2>
          <Button className="text-xl px-12 py-5 w-full md:w-auto" data-id="element-110">
            ELIJO MÁS CALMA – $13.999
          </Button>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#EBE7DE] text-center text-[#6B6B6B] text-sm" data-id="element-111">
        <p data-id="element-112">
          © {new Date().getFullYear()} Cabeza en Calma. Todos los derechos
          reservados.
        </p>
      </footer>
    </div>;
}